import { useMemo } from "react";
import {
  SelectFieldGroupByValue,
  SelectFieldOption,
} from "../../beta/components/SelectField/types";
import { SelectItem } from "../types/selectFieldInternalTypes";

/**
 * Normalized group section structure used internally.
 * @property label - The group label (derived from groupToString or String(groupValue))
 * @property options - The options belonging to this group
 */
export type NormalizedGroupSection = {
  label: string;
  options: SelectFieldOption[];
};

type UseGroupedOptionsResult = {
  /** Options that have a group property, converted to downshift items */
  groupedItems: SelectItem[];
  /** Options that don't have a group property, converted to downshift items */
  ungroupedItems: SelectItem[];
  /** Metadata about each group section for rendering */
  groupSections: NormalizedGroupSection[];
};

/**
 * Hook that processes options and groups them by their `group` property.
 *
 * Groups are ordered by insertion order (first occurrence of a group value
 * defines its position in the list).
 *
 * @param options - The options to process
 * @param groupToString - Optional function to convert group values to display labels
 * @returns Grouped items, ungrouped items, and group section metadata
 */
export function useGroupedOptions(
  options: SelectFieldOption[],
  groupToString?: (groupValue: SelectFieldGroupByValue) => string,
): UseGroupedOptionsResult {
  return useMemo(() => {
    const groupedItems: SelectItem[] = [];
    const ungroupedItems: SelectItem[] = [];
    const groupSections: NormalizedGroupSection[] = [];

    // Map to track groups by their value, preserving insertion order
    const groupMap = new Map<
      SelectFieldGroupByValue,
      { label: string; options: SelectFieldOption[] }
    >();

    options.forEach((option) => {
      if (option.group !== undefined) {
        // Option has a group
        const groupValue = option.group;

        if (!groupMap.has(groupValue)) {
          // First occurrence of this group - create new entry
          const label = groupToString
            ? groupToString(groupValue)
            : String(groupValue);
          groupMap.set(groupValue, { label, options: [] });
        }

        // Add option to its group
        groupMap.get(groupValue)!.options.push(option);

        // Add to grouped items
        groupedItems.push({
          id: option.id,
          type: "grouped-option",
          original: option,
          disabled: option.disabled ?? false,
        });
      } else {
        // Option has no group - add to ungrouped
        ungroupedItems.push({
          id: option.id,
          type: "option",
          original: option,
          disabled: option.disabled ?? false,
        });
      }
    });

    // Convert map to array (maintains insertion order)
    groupMap.forEach(({ label, options: groupOptions }) => {
      groupSections.push({ label, options: groupOptions });
    });

    return { groupedItems, ungroupedItems, groupSections };
  }, [options, groupToString]);
}

/**
 * Sorts group sections using the provided comparator function.
 * The group value is extracted from the first option in each section.
 *
 * @param sections - The group sections to sort
 * @param groupSorter - Comparator function for group values
 * @returns A new array of sorted sections
 */
export function sortGroupSections(
  sections: NormalizedGroupSection[],
  groupSorter: (
    a: SelectFieldGroupByValue,
    b: SelectFieldGroupByValue,
  ) => number,
): NormalizedGroupSection[] {
  return [...sections].sort((a, b) => {
    const groupA = a.options[0]?.group;
    const groupB = b.options[0]?.group;
    if (groupA === undefined || groupB === undefined) return 0;
    return groupSorter(groupA, groupB);
  });
}
